import {
  Component,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  inject,
  OnChanges,
} from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import {
  AppConfigService,
  FormFieldValidator,
  LocalizedDatePipe,
  RequiredFieldValidator,
} from '@alfresco/adf-core';
import {
  StartProcessInstanceComponent,
  ProcessService,
  ActivitiContentService,
  AppsProcessService,
} from '@alfresco/adf-process-services';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import {
  ProcessDefinitionRepresentation,
  RestVariable,
  ProcessInstanceRepresentation,
} from '@alfresco/js-api';

import { AppStore } from '../../store/states/app.state';
import { currentSiteSelector } from '../../store/selectors/app.custom-selectors';
import { ProcessListService } from '../shared/services/process-list.service';
import { WkAppConfigService } from '../../services';
import { IStartProcessConfig } from './models/start-process-config.model';
import { ReloadDocumentListAction } from '../../store';
import { WkRequireApprovedPercentValidator } from './validator/wk-required-approve-percent-validator';
import { WkDueDateValidator } from './validator/wk-due-date-validator';

@Component({
  selector: 'wk-start-process',
  templateUrl: './wk-start-process.component.html',
  styleUrls: ['./wk-start-process.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WkStartProcessInstanceComponent
  extends StartProcessInstanceComponent
  implements OnInit, OnChanges, OnDestroy
{
  public processStarted = false;
  public fieldValidators: FormFieldValidator[] = [];
  private readonly _onDestroy = new Subject<void>();
  private readonly store: Store<AppStore> = inject(Store);
  private readonly processListService: ProcessListService =
    inject(ProcessListService);
  private readonly config: WkAppConfigService = inject(WkAppConfigService);
  constructor(
    private readonly wkActivitiProcess: ProcessService,
    activitiContentService: ActivitiContentService,
    appsProcessService: AppsProcessService,
    appConfig: AppConfigService,
    localizedDatePipe: LocalizedDatePipe
  ) {
    super(
      wkActivitiProcess,
      activitiContentService,
      appsProcessService,
      appConfig,
      localizedDatePipe
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.variables = [
      {
        name: 'siteId',
        type: 'string',
        scope: 'global',
        value: '',
      } as RestVariable,
    ];
    this.store
      .select(currentSiteSelector)
      .pipe(
        filter(
          (currentSite) => currentSite !== '' && currentSite !== 'no-site'
        ),
        takeUntil(this._onDestroy)
      )
      .subscribe(
        (currentSite: string) => (this.variables[0].value = currentSite)
      );
    this.processNameInput = new UntypedFormControl('', [
      Validators.required,
      Validators.maxLength(this.maxProcessNameLength),
      Validators.pattern('^[^\\s]+(\\s+[^\\s]+)*$'),
    ]);
    this.processDefinitionInput = new UntypedFormControl();

    this.fieldValidators = [
      new WkDueDateValidator(),
      new RequiredFieldValidator(),
      new WkRequireApprovedPercentValidator(),
    ];

    this.filteredProcessesDefinitions$ =
      this.processDefinitionInput.valueChanges.pipe(
        map((value: string) => this.filter(value))
      );
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  filter(value: string): ProcessDefinitionRepresentation[] {
    if (value !== null && value !== undefined) {
      const filterValue = value.toLowerCase();
      this.name = filterValue;
      const filteredProcess = this.processDefinitions.filter((option) =>
        option.name.toLowerCase().includes(filterValue)
      );

      if (!this.findProcessDefinition(value)) {
        this.selectedProcessDef = this['getSelectedProcess'](filterValue);
      }

      return filteredProcess;
    }

    return [];
  }

  _resetSelectedProcessDefinition() {
    this.selectedProcessDef = undefined;
    if (this.processDefinitionInput) {
      this.processDefinitionInput.setValue('');
    }
  }

  loadProcessDefinitions(appId: any): void {
    this.isProcessDefinitionsLoading = true;
    this._resetSelectedProcessDefinition();
    this.wkActivitiProcess
      .getProcessDefinitions(appId)
      .pipe(
        map(
          (
            processDefinitionRepresentations: ProcessDefinitionRepresentation[]
          ) => {
            let currentProcessDef: ProcessDefinitionRepresentation;
            if (this.processDefinitionName) {
              const filteredProcessDefinition =
                processDefinitionRepresentations.find(
                  (processDefinition: ProcessDefinitionRepresentation) => {
                    return (
                      processDefinition.name === this.processDefinitionName
                    );
                  }
                );
              if (filteredProcessDefinition) {
                currentProcessDef = filteredProcessDefinition;
              }
            }
            return { currentProcessDef, processDefinitionRepresentations };
          }
        ),
        takeUntil(this._onDestroy)
      )
      .subscribe({
        next: (filteredProcessDefinitions) => {
          this.processDefinitions =
            filteredProcessDefinitions.processDefinitionRepresentations;
          this.processDefinitionSelectionChanged(
            filteredProcessDefinitions.currentProcessDef
          );
          this.processDefinitionInput.setValue(
            this.selectedProcessDef ? this.selectedProcessDef.name : ''
          );
          this.isProcessDefinitionsLoading = false;
        },
        error: (error) => {
          this.isProcessDefinitionsLoading = false;
          this.error.emit(error);
        },
      });
  }

  startProcess(outcome?: string) {
    this.processStarted = true;
    const startProcessConfig = this.config.get<IStartProcessConfig>(
      'startProcessOptions'
    );
    startProcessConfig?.multipleInstances
      ? this.startProcesMultiInstances()
      : this.startProcessSingleInstance(outcome);
  }

  /**
   * This function is in charge of calling the ACS service
   * for start proces with a single instance.
   *
   * @param outcome (string) optional
   */
  startProcessSingleInstance(outcome?: string) {
    if (
      this.selectedProcessDef &&
      this.selectedProcessDef.id &&
      this.nameController.value
    ) {
      const formValues = this.startForm?.form.values;
      formValues.documentattached = this.movedNodeToPS.documentattached
        .map((element: any) => element.id)
        .join(';');
      this.wkActivitiProcess
        .startProcess(
          this.selectedProcessDef.id,
          this.nameController.value,
          outcome,
          formValues
        )
        .pipe(
          take(1),
          switchMap((res: ProcessInstanceRepresentation) => {
            this.name = '';
            this.start.emit(res);
            return this.processListService.setProcessInstanceVariables(
              res.id,
              this.variables
            );
          }),
          takeUntil(this._onDestroy)
        )
        .subscribe({
          next: () => {},
          error: (err) => this.error.emit(err),
        });

      this.store.dispatch(new ReloadDocumentListAction());
    }
  }

  /**
   * This function is in charge of call the function
   * in process-list-service for new API service
   * for start proces with multiple instances.
   */
  startProcesMultiInstances() {
    if (this.selectedProcessDef?.id && this.nameController.value) {
      const formValues = this.startForm?.form.values;
      formValues.documentattached = this.movedNodeToPS.documentattached
        .map((element: any) => element.id)
        .join(',');
      this.processListService
        .startProcessWithMultipleInstances(
          this.selectedProcessDef.id,
          this.nameController.value,
          formValues
        )
        .pipe(take(1), takeUntil(this._onDestroy))
        .subscribe({
          next: (res: ProcessInstanceRepresentation) => {
            this.name = '';
            this.start.emit(res);
          },
          error: (err) => this.error.emit(err),
        });

      this.store.dispatch(new ReloadDocumentListAction());
    }
  }

  private findProcessDefinition(
    value: string
  ): ProcessDefinitionRepresentation | null {
    return this.processDefinitions.find(
      ({ name }) => name.toLowerCase() === value
    );
  }
}
